import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, PasswordChangeRequiredError, UnauthorizedError } from '@/lib/api';

/**
 * What happens when the hub stops recognising this browser.
 *
 * `request()` has always thrown `UnauthorizedError` on a 401, but until now only
 * the mount-time probe in `SessionGate` caught one. A cookie that expired while
 * the tab was open therefore left the app in a state it could not leave: every
 * query and every write failed into a toast reading "unauthorized", and the login
 * gate never came back. A 401 is never about the query that happened to notice
 * it first, so it is handled here, once, for the whole cache.
 *
 * The handler is registered by `SessionGate` rather than imported by it: the
 * query client is a module singleton and the session is React state, so the
 * dependency has to run in that direction. One handler at a time, and it is the
 * handler's job to fire once per expiry rather than once per failed query --
 * fourteen queries failing together are one expired cookie.
 */
type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

/**
 * The same arrangement for the other answer that is about the session rather
 * than the query: the hub refusing everything until a temporary password is
 * changed. It happens mid-session when an admin resets somebody who is signed
 * in, and every mounted query fails together; the gate re-reads `/api/me`,
 * learns the flag is set, and the shell sends the person to the card.
 */
type PasswordChangeRequiredHandler = () => void;

let passwordChangeRequiredHandler: PasswordChangeRequiredHandler | null = null;

export function setPasswordChangeRequiredHandler(
  handler: PasswordChangeRequiredHandler | null,
): void {
  passwordChangeRequiredHandler = handler;
}

/**
 * Everything else a query can fail with: a 500, a 422, a dropped connection.
 *
 * These used to fall off the end of `onCacheError` and be seen by nobody. The
 * page then coerced `data ?? []` and drew its empty state, so a hub that was
 * down looked exactly like a hub with nothing in it. The pages say so inline
 * now (`LoadError`), and this is the other half: the tab you are not looking at
 * failing is still worth one line.
 *
 * Keyed by query key, so the fourteen queries one outage takes down are not
 * fourteen toasts stacked over the app - and the key is the identity, so a
 * different page failing later still says so. Cleared when that query next
 * succeeds, by the `onSuccess` below; without that, a page that failed, was
 * fixed and failed again an hour later would go quiet.
 */
const toastedKeys = new Set<string>();

/**
 * Records that `key` failed, and answers whether this failure is the one worth
 * saying out loud. The bookkeeping is split from the toast so a test can drive
 * the dedupe without a rendered toaster.
 */
export function noteQueryFailure(key: string): boolean {
  if (toastedKeys.has(key)) return false;
  toastedKeys.add(key);
  return true;
}

/** That key answered again: the next failure on it is news. */
export function noteQuerySuccess(key: string): void {
  toastedKeys.delete(key);
}

/**
 * Every cache error passes through here. Exported so a test can drive the path a
 * failing query takes without standing up a rendered app.
 *
 * `key` is absent for a mutation, which has its own error surface at the button
 * that started it and must not toast twice.
 */
export function onCacheError(error: unknown, key?: string): void {
  if (error instanceof UnauthorizedError) {
    unauthorizedHandler?.();
    return;
  }
  if (error instanceof PasswordChangeRequiredError) {
    passwordChangeRequiredHandler?.();
    return;
  }
  if (key === undefined || !noteQueryFailure(key)) return;
  toast.error('Could not load this page', {
    description: error instanceof Error ? error.message : 'the hub did not answer',
  });
}

/**
 * The console is a live surface: an SSE stream invalidates these keys the moment
 * the hub records anything, and the 30s refetch is only the fallback for a stream
 * that dropped. Both roles have one now -- an admin streams the org-wide journal,
 * a member streams their own slice of it -- so the interval means the same thing
 * on every page. `staleTime` is short and refetch-on-focus is on either way:
 * coming back to the tab should not show a minute-old fleet.
 *
 * Nothing the hub refuses is worth retrying. A 401 will not have a better
 * credential by the second attempt, a 403 will not have a better role, and a 422
 * will not have a better body; retrying only doubles the requests racing the
 * handler above and the number of denied pages that cost two round trips. A 5xx
 * or a dropped connection keeps the single retry the console has always had.
 */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => onCacheError(error, query.queryHash),
    onSuccess: (_data, query) => noteQuerySuccess(query.queryHash),
  }),
  mutationCache: new MutationCache({ onError: (error) => onCacheError(error) }),
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        if (error instanceof UnauthorizedError) return false;
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 1;
      },
    },
  },
});

export const hubKeys = {
  me: ['me'] as const,
  health: ['health'] as const,
  stats: ['stats'] as const,
  // Keyed by the slice, not just the window: two filters are two different
  // answers, and caching them under one key would show a reader last project's
  // numbers under this project's heading. `['metrics']` is still the prefix
  // `LIVE_KEYS` invalidates, so every cached slice refetches on an event.
  metrics: (days: number, repo?: string | null, person?: string | null) =>
    ['metrics', days, repo ?? null, person ?? null] as const,
  review: ['review'] as const,
  workers: ['workers'] as const,
  briefs: ['briefs'] as const,
  brief: (id: string) => ['brief', id] as const,
  tasks: ['tasks'] as const,
  task: (id: string) => ['task', id] as const,
  events: ['events'] as const,
  projects: ['projects'] as const,
  project: (repo: string) => ['project', repo] as const,
  repos: ['repos'] as const,
  fleetPrompt: ['fleet-prompt'] as const,
  users: ['users'] as const,
  invites: ['invites'] as const,
  schedules: ['schedules'] as const,
  schedule: (id: string) => ['schedule', id] as const,
  schedulePresets: ['schedule-presets'] as const,
  myDevices: ['me', 'devices'] as const,
  myTasks: ['me', 'tasks'] as const,
  myBriefs: ['me', 'briefs'] as const,
  myEvents: ['me', 'events'] as const,
  /** What to bring to a first device: the fleet policy, tool variables, repos. */
  onboarding: ['onboarding'] as const,
  /** The steps an enrollment link's code implies, keyed by the code itself. */
  enrollmentSteps: (code: string) => ['enrollment-steps', code] as const,
} as const;

/**
 * Everything a write can move. One list, so a mutation does not have to know
 * which view is mounted: approving a brief changes the brief, its tasks, the
 * gate counts, the stats strip and the project rollups, and the cheapest correct
 * answer is to invalidate the lot and let react-query refetch what is on screen.
 */
export const LIVE_KEYS = [
  hubKeys.stats,
  hubKeys.review,
  hubKeys.workers,
  hubKeys.briefs,
  hubKeys.tasks,
  hubKeys.events,
  hubKeys.health,
  hubKeys.projects,
  hubKeys.repos,
  hubKeys.fleetPrompt,
  hubKeys.users,
  hubKeys.invites,
  hubKeys.schedules,
  ['schedule'],
  ['brief'],
  ['task'],
  ['project'],
  ['me'],
  ['metrics'],
] as const;

/**
 * What the stream's debounced nudge moves: the fleet overview's reads, and the
 * two detail prefixes.
 *
 * The details were missing, and every surface built on one was live only in the
 * sense that it refetched when the person did something. `['brief', id]` and
 * `['task', id]` are only ever mounted one at a time - the pane you have open,
 * the sheet over it - so this is at most two more requests per burst, and both
 * prefixes already ride `LIVE_KEYS` on every write. The graph page
 * (docs/JOB-LIFECYCLE.md 2.8) is what made the gap matter: it draws the nodes
 * off the brief detail and the sheet over them off the task detail, so a step a
 * worker moved changed neither until something else on the page did.
 */
export const FLEET_KEYS = [
  hubKeys.stats,
  hubKeys.review,
  hubKeys.workers,
  hubKeys.briefs,
  hubKeys.tasks,
  hubKeys.health,
  hubKeys.projects,
  hubKeys.repos,
  ['brief'],
  ['task'],
] as const;

/**
 * What a member's own stream can move: the shared reads, plus their own slice.
 *
 * `['me']` is a prefix, so it covers `/me`, their devices, their tasks and their
 * briefs in one entry. `workers` is in here through `FLEET_KEYS` and stays
 * harmless: a member's device query is disabled, and invalidating a disabled
 * query refetches nothing.
 */
export const MY_KEYS = [...FLEET_KEYS, ['me']] as const;
